// src/pages/PaymentsReceived.jsx
import React, { useState, useEffect } from 'react';
import {
  collection,
  query,
  where,
  onSnapshot,
} from 'firebase/firestore';
import { db } from '../services/firebase';
import Layout from '../component/Layout';

const PaymentsReceived = () => {
  const [groupedOrders, setGroupedOrders] = useState({});

  const operatorId = localStorage.getItem("operatorId");

  const getSizeRows = (order) => {
    const rows = [];
    const variants = order.selectedVariants || [];
    const lots = order.lotDetails || [];
    const lotBySize = {};
    lots.forEach((l) => { if (l.size != null) lotBySize[l.size] = l; });

    if (variants.length) {
      variants.forEach((v) => {
        const lot = lotBySize[v.size];
        rows.push({
          size: v.size,
          qty: v.quantity,
          netWt: lot?.netWt ?? lot?.avgNetWt ?? order.netWt,
          grossWt: lot?.grossWt ?? lot?.avgGrossWt ?? order.grossWt,
        });
      });
    } else if (lots.length) {
      lots.forEach((l) => {
        rows.push({
          size: l.size,
          qty: l.set ?? l.quantity ?? 1,
          netWt: l.netWt ?? l.avgNetWt,
          grossWt: l.grossWt ?? l.avgGrossWt,
        });
      });
    } else if (order.orderDetail && typeof order.orderDetail === 'object') {
      Object.entries(order.orderDetail).forEach(([size, d]) => {
        rows.push({
          size,
          qty: d.qty ?? d.set ?? 1,
          netWt: d.netWt ?? d.netwt,
          grossWt: d.grossWt ?? d.grossWT,
        });
      });
    }
    return rows;
  };

  const num = (val) => {
    if (val === null || val === undefined || val === '') return 0;
    const parsed = parseFloat(val);
    return isNaN(parsed) ? 0 : parsed;
  };

  const calculateGroupTotals = (group) => {
    let totalOrders = 0;
    let totalSets = 0;
    let totalNetWt = 0;
    let totalGrossWt = 0;

    group.orders.forEach((order) => {
      totalOrders += 1;
      const sizeRows = getSizeRows(order);
      if (sizeRows.length > 0) {
        sizeRows.forEach((r) => {
          totalSets += num(r.qty);
          totalNetWt += num(r.netWt);
          totalGrossWt += num(r.grossWt);
        });
      } else {
        totalNetWt += num(order.netWt);
        totalGrossWt += num(order.grossWt);
        totalSets += num(order.quantity) || 1;
      }
    });

    return { totalOrders, totalSets, totalNetWt, totalGrossWt };
  };

  useEffect(() => {
    // Build the collection ref inside the effect. Declaring it in the component
    // body created a new object every render, so the listener was torn down and
    // re-created in a loop.
    // Scope strictly to this operator; Firestore throws if a where() value is
    // undefined, so wait until the operator is available.
    if (!operatorId) {
      setGroupedOrders({});
      return undefined;
    }

    const q = query(
      collection(db, 'orderList'),
      where('operatorId', '==', operatorId),
      where('orderStatus', '==', 'paymentDispatchedtoOperator')
    );

    const unsubscribeOrders = onSnapshot(q, (snapshot) => {
      const ordersData = [];
      snapshot.forEach((docSnap) => {
        const data = docSnap.data();
        let orderTime = null;
        if (data.orderTime?.seconds) {
          orderTime = new Date(data.orderTime.seconds * 1000);
        } else if (data.addedAt) {
          orderTime = new Date(data.addedAt);
        }

        ordersData.push({
          docId: docSnap.id,
          ...data,
          orderTime,
        });
      });

      const groups = {};
      ordersData.forEach((order) => {
        const seller = order.sellerId ;
        const groupKey = `${seller}`;

        if (!groups[groupKey]) {
          groups[groupKey] = {
            seller,
            sellerId: order.sellerId,
            orders: [],
          };
        }
        groups[groupKey].orders.push(order);
      });

      Object.keys(groups).forEach((key) => {
        groups[key].orders.sort(
          (a, b) => (a.orderTime || 0) - (b.orderTime || 0)
        );
      });

      setGroupedOrders(groups);
    });

    return () => unsubscribeOrders();
  }, [operatorId]);

  const formatExpiry = (expiresAt) => {
    if (!expiresAt) return 'Expired';
    const expiresTime = expiresAt.seconds ? expiresAt.seconds * 1000 : expiresAt;
    const diffMs = expiresTime - Date.now();
    const mins = Math.max(0, Math.floor(diffMs / 60000));
    const secs = Math.max(0, Math.floor((diffMs % 60000) / 1000));
    return `${mins}m ${secs}s`;
  };

  const formatDate = (date) => (date ? date.toLocaleString('en-IN') : '—');
  const getAge = (orderTime) => {
    if (!orderTime) return '—';
    const diffMs = new Date() - orderTime;
    const mins = Math.floor(diffMs / 60000);
    const secs = Math.floor((diffMs % 60000) / 1000);
    return `${mins}m ${secs}s`;
  };

  const getStatusStyle = (status) => ({
    display: 'inline-block',
    padding: '2px 6px',
    fontSize: '11px',
    fontWeight: 'bold',
    borderRadius: '4px',
    textTransform: 'capitalize',
    backgroundColor:
      status === 'Requested'
        ? '#fef3c7'
        : status === 'Accepted'
          ? '#dcfce7'
          : status === 'Assorted'
            ? '#ede9fe'
            : '#e5e7eb',
    color:
      status === 'Requested'
        ? '#92400e'
        : status === 'Accepted'
          ? '#166534'
          : status === 'Assorted'
            ? '#5b21b6'
            : '#374151',
  });

  const groupEntries = Object.entries(groupedOrders);

  return (
    <Layout pageTitle="Payments Received">
      <div style={{ padding: '8px' }}>
        {groupEntries.length === 0 ? (
          <p
            style={{
              textAlign: 'center',
              color: '#6b7280',
              paddingTop: '32px',
              fontSize: '18px',
            }}
          >
            No payments pending.
          </p>
        ) : (
          groupEntries.map(([groupKey, group]) => {


            return (
                <div
                key={groupKey}
                style={{
                    marginBottom: '12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    padding: '12px',
                    backgroundColor: '#ffffff',
                    borderLeft: `4px solid #6b7280`,
                }}
            >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: '16px',
                    flexWrap: 'wrap',
                    gap: '16px',
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <h3 style={{ margin: '0 0 8px', color: '#1f2937', fontSize: '20px', fontWeight: 'bold' }}>
                      {group.seller}
                    </h3>
                    <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                      <div style={{ color: '#6b7280', fontSize: '14px' }}>
                        <strong>JRE ID:</strong> {group.jreId}
                      </div>
                      <div style={{ color: '#6b7280', fontSize: '14px' }}>
                        <strong>Orders:</strong> {group.orders.length}
                      </div>
                      
                    </div>
                  </div>
                  
                </div>

                <div style={{ overflowX: 'auto' }}>
                  <table
                    style={{
                      minWidth: '100%',
                      borderCollapse: 'collapse',
                      border: '1px solid #1f2937',
                    }}
                  >
                    <thead>
                      <tr style={{ backgroundColor: '#f3f4f6' }}>
                        <th style={tableHeaderStyle}>Order Time</th>
                        <th style={tableHeaderStyle}>Age</th>
                        <th style={tableHeaderStyle}>Product</th>
                        <th style={tableHeaderStyle}>Image</th>
                        <th style={tableHeaderStyle}>Buyer</th>
                        <th style={tableHeaderStyle}>Net Wt (g)</th>
                        <th style={tableHeaderStyle}>Gross Wt (g)</th>
                        <th style={tableHeaderStyle}>Status</th>
                        <th style={tableHeaderStyle}>Variants</th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.orders.map((order, index) => {
                        const isOverdue =
                          order.orderTime && new Date() - order.orderTime > 60000;
                        const rowBorder = isOverdue
                          ? '2px solid #ef4444'
                          : '1px solid #1f2937';
                        const sizeRows = getSizeRows(order);

                        return (
                          <React.Fragment key={order.docId}>
                            <tr
                              style={{
                                backgroundColor: isOverdue ? '#fee2e2' : '#ffffff',
                                border: rowBorder,
                                outline: isOverdue ? '2px solid #fecaca' : 'none',
                                outlineOffset: '-2px',
                              }}
                            >
                              <td style={tableCellStyle}>{formatDate(order.orderTime)}</td>
                              <td style={{ ...tableCellStyle, fontFamily: 'monospace' }}>
                                {getAge(order.orderTime)}
                              </td>
                              <td style={{ ...tableCellStyle, fontWeight: 'bold' }}>
                                {order.productName || '—'}
                              </td>
                              <td style={tableCellStyle}>
                                {order.images?.[0]?.url ? (
                                  <img
                                    src={order.images[0].url.trim()}
                                    alt="Product"
                                    style={{
                                      height: '24px',
                                      width: '24px',
                                      objectFit: 'contain',
                                      border: '1px solid #d1d5db',
                                      borderRadius: '3px',
                                    }}
                                  />
                                ) : (
                                  <span style={{ color: '#9ca3af' }}>—</span>
                                )}
                              </td>
                              <td style={tableCellStyle}>{order.buyerId || '—'}</td>
                              <td style={tableCellStyle}>{order.netWt || '—'}</td>
                              <td style={tableCellStyle}>{order.grossWt || '—'}</td>
                              <td style={tableCellStyle}>
                                <span style={getStatusStyle(order.orderStatus)}>
                                  {order.orderStatus}
                                </span>
                              </td>
                              <td style={tableCellStyle}>
                                {order.selectedVariants?.length ? (
                                  order.selectedVariants.map((v, i) => (
                                    <div key={i} style={{ whiteSpace: 'nowrap' }}>
                                      {v.size} × {v.quantity}
                                    </div>
                                  ))
                                ) : (
                                  <span>—</span>
                                )}
                              </td>
                            </tr>
                            {/* Size-wise breakdown rows */}
                            {sizeRows.map((r, i) => (
                              <tr
                                key={`${order.docId}-size-${i}`}
                                style={{
                                  backgroundColor: '#fafafa',
                                  border: '1px solid #e5e7eb',
                                }}
                              >
                                <td style={{ ...tableCellStyle, border: 'none', color: '#6b7280' }}></td>
                                <td style={{ ...tableCellStyle, border: 'none' }}></td>
                                <td style={{ ...tableCellStyle, border: 'none', color: '#6b7280', paddingLeft: '24px' }} colSpan={2}>
                                  ↳ Size: <strong>{r.size}</strong>
                                </td>
                                <td style={{ ...tableCellStyle, border: 'none' }}></td>
                                <td style={{ ...tableCellStyle, border: 'none', color: '#6b7280' }}>{r.netWt ?? '—'}</td>
                                <td style={{ ...tableCellStyle, border: 'none', color: '#6b7280' }}>{r.grossWt ?? '—'}</td>
                                <td style={{ ...tableCellStyle, border: 'none' }}></td>
                                <td style={{ ...tableCellStyle, border: 'none', color: '#6b7280' }}>
                                  Qty: {r.qty}
                                </td>
                              </tr>
                            ))}
                          </React.Fragment>
                        );
                      })}
                      {/* Group-wise total row */}
                      <tr style={{ backgroundColor: '#dbeafe' }}>
                        <td style={{ ...tableCellStyle, border: 'none', fontWeight: 'bold' }} colSpan={5}>
                          Total ({group.seller})
                        </td>
                        <td style={{ ...tableCellStyle, border: 'none', fontWeight: 'bold' }}>
                          Net: {calculateGroupTotals(group).totalNetWt.toFixed(3)}g
                        </td>
                        <td style={{ ...tableCellStyle, border: 'none', fontWeight: 'bold' }}>
                          Gross: {calculateGroupTotals(group).totalGrossWt.toFixed(3)}g
                        </td>
                        <td style={{ ...tableCellStyle, border: 'none', fontWeight: 'bold' }} colSpan={2}>
                          Sets: {calculateGroupTotals(group).totalSets.toFixed(3)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>


              </div>
            );
          })
        )}
      </div>
    </Layout>
  );
};

const tableHeaderStyle = {
  border: '1px solid #1f2937',
  padding: '4px 6px',
  textAlign: 'left',
  fontSize: '11px',
  fontWeight: 'bold',
  backgroundColor: '#f3f4f6',
  color: '#4b5563',
};

const tableCellStyle = {
  border: '1px solid #1f2937',
  padding: '4px 6px',
  fontSize: '12px',
};

export default PaymentsReceived;